import { api } from './client';

// ── Admin S3 file browser ────────────────────────────────────────────────
//
// Backed by AdminS3Controller at /api/admin/s3. Lists / reads / writes /
// deletes objects directly in the application bucket. Admin-only — gated by
// the AdminAuthorizationFilter, same as every /api/admin endpoint.

export interface AdminS3Object {
  key: string;
  size: number;
  lastModified: string | null;
  etag: string | null;
  contentType?: string | null;
}

export interface AdminS3ListResponse {
  prefix: string;
  folders: string[];
  files: AdminS3Object[];
}

export interface AdminS3Metadata {
  key: string;
  size: number;
  lastModified: string | null;
  etag: string | null;
  contentType: string | null;
  metadata: Record<string, string>;
}

export interface AdminS3TextResponse {
  key: string;
  content: string;
  size: number;
  contentType: string | null;
  etag: string | null;
}

export const adminS3Api = {
  list: (prefix: string) =>
    api.get<AdminS3ListResponse>('/api/admin/s3/list', { params: { prefix } }).then((r) => r.data),
  metadata: (key: string) =>
    api.get<AdminS3Metadata>('/api/admin/s3/metadata', { params: { key } }).then((r) => r.data),
  readText: (key: string) =>
    api.get<AdminS3TextResponse>('/api/admin/s3/text', { params: { key } }).then((r) => r.data),
  writeText: (body: { key: string; content: string; contentType?: string }) =>
    api.put<AdminS3Object>('/api/admin/s3/text', body).then((r) => r.data),
  upload: (file: File, folder: string, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return api
      .post<AdminS3Object>('/api/admin/s3/upload', fd, {
        params: { folder },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      })
      .then((r) => r.data);
  },
  createFolder: (prefix: string, name: string) =>
    api.post<AdminS3Object>('/api/admin/s3/folders', { prefix, name }).then((r) => r.data),
  rename: (from: string, to: string, isFolder: boolean) =>
    api
      .post<{
        source: string;
        target: string;
        copied: number;
        deleted: number;
      }>('/api/admin/s3/rename', { from, to, isFolder })
      .then((r) => r.data),
  copy: (from: string, to: string, isFolder: boolean) =>
    api
      .post<{
        source: string;
        target: string;
        copied: number;
      }>('/api/admin/s3/copy', { from, to, isFolder })
      .then((r) => r.data),
  deleteObject: (key: string) =>
    api
      .delete<{ key: string; deleted: boolean }>('/api/admin/s3/object', { params: { key } })
      .then((r) => r.data),
  deleteFolder: (prefix: string) =>
    api
      .delete<{ prefix: string; deleted: number }>('/api/admin/s3/folder', { params: { prefix } })
      .then((r) => r.data),
  /**
   * Fetch object bytes with auth header. Returns a Blob; the caller can
   * convert it to an object URL for preview, or trigger a download via an
   * <a> tag. Used by both the inspector preview and the Save-to-disk button.
   */
  fetchBlob: (key: string) =>
    api
      .get('/api/admin/s3/download', {
        params: { key, download: 'false' },
        responseType: 'blob',
      })
      .then((r) => r.data as Blob),
};
